import { combineEpics } from 'redux-observable';
import { Observable } from 'rxjs';

import * as ActionCreators from '../../actions/ActionCreators';
import * as ActionTypes from '../../actions/ActionTypes';
import * as VideoTypes from './videoTypes';
import { serialize, handleErrors, isHLSSupported } from '../../utils/ASVPUtils';

import {
    UNSUPPORTED_CSS_CLASS,
    ERROR_CSS_CLASS,
    ERROR_FETCHING_SOURCE_SLATE,
    HLS_MIME_TYPE,
    VIDEO_SOURCE_QUERY_PARAMS,
    VIDEO_SOURCE_URL,
    MEDIA_JSON_SOURCE_URL
} from '../../constants';


const setSourceFromVideoTagEpic = (action$, store, { player, options }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_READY)
    .filter(() => !options.videoID)
    .filter(() => !store.getState().player.videoID)
    .map(() => player.tech('Html5').currentSrc())
    .filter(src => !!src)
    .do(src => player.src({ 'src': src }))
    .ignoreElements();


// when player is ready and/or video id is set -- fetch the data based on the videoID in the state
// the initial state might have the video id from how it's passed in on player creation (PLAYER_EVENT_READY)
// SET_VIDEO_ID will have set the video id to a new one.
const fetchVideoSourceEpic = (action$, store, { }) => action$
    .ofType(ActionTypes.SET_VIDEO_ID)
    .map(() => store.getState().player.videoID)
    .distinctUntilChanged() // stop here if the video id hasn't changed
    .filter(Boolean) // if video id exists
    .switchMap(videoID => Observable.fromPromise(
        fetch(`${VIDEO_SOURCE_URL}${videoID}?${serialize(VIDEO_SOURCE_QUERY_PARAMS)}`) // gets new video data from id
            .then(handleErrors)
            .then(res => res.json())
            .then(response => ActionCreators.receiveVideoSource(response))
            .catch(data => ActionCreators.fetchVideoSourceError(data))
    ));


const fetchMediaSourceEpic = (action$, store, { }) => action$
    .ofType(ActionTypes.SET_VIDEO_ID)
    .map(() => store.getState().player.videoID)
    .distinctUntilChanged() // stop here if the video id hasn't changed
    .filter(Boolean) // if video id exists
    .switchMap(videoID => Observable.fromPromise(
        fetch(`${MEDIA_JSON_SOURCE_URL}${videoID}/desktop`) // gets new video data from id
            .then(handleErrors)
            .then(res => res.json())
            .then(response => ActionCreators.receiveMediaSource(response))
            .catch(data => ActionCreators.fetchVideoSourceError(data))
    ));


// called when video and media source are finished processing
// because we need both sources for playback
const receiveVideoPlayerDataEpic = (action$, store, { }) => action$
    .ofType(ActionTypes.SET_VIDEO_ID)
    .map(() => store.getState().player.videoID)
    .distinctUntilChanged() // stop here if the video id hasn't changed
    .filter(Boolean) // if video id exists
    .switchMap(() => Observable.combineLatest(
        action$.ofType(ActionTypes.RECEIVE_VIDEO_SOURCE),
        action$.ofType(ActionTypes.RECEIVE_MEDIA_SOURCE),
        action$.ofType(ActionTypes.RECEIVE_CDN_TOKEN_SOURCE)
    ).take(1))
    .map(() => ActionCreators.receiveVideoPlayerData()); // get video source state from store


// sets slate for video source error
const fetchVideoSourceError = (action$, __, { player }) => action$
    .ofType(ActionTypes.FETCH_VIDEO_SOURCE_ERROR)
    .do(data => console.error('fetchVideoSourceError', data))
    .do(() => player.addClass(ERROR_CSS_CLASS))
    .do(() => player.pause())
    .do(() => player.poster(ERROR_FETCHING_SOURCE_SLATE))
    .ignoreElements();


// sets the playback url either using the cdn token or the auditude url
const setPlaybackURLEpic = (action$, store, { auditude }) => action$
    .ofType(ActionTypes.RECEIVE_VIDEO_PLAYER_DATA)
    .map(() => store.getState())
    .switchMap(state => {
        let useSSAI = true;

        if (!state.player.adsEnabled ||
            state.player.streamBlocked ||
            state.videoSource.disableAds ||
            state.videoSource.type !== VideoTypes.STREAM) {
            useSSAI = false;
        }

        return (useSSAI) ?
            Observable.of(ActionCreators.setPlaybackURL(auditude.buildURL(state.videoSource, state.cdnToken.token))) :
            Observable.of(ActionCreators.setPlaybackURL(`${state.videoSource.contentURL}?hdnts=${state.cdnToken.token}`))
    });


const updatePlaybackURLEpic = (action$, store, {}) => action$
    .ofType(ActionTypes.ADS_MIDROLL_END)
    .switchMap(() => Observable.combineLatest(
        action$.ofType(ActionTypes.RECEIVE_CDN_TOKEN_SOURCE),
    ).take(1))
    .map(() => store.getState())
        .switchMap(state => {
            return Observable.of(ActionCreators.setPlaybackURL(`${state.videoSource.contentURL}?hdnts=${state.cdnToken.token}`))
        });


// set/unset unsupported css class if can't play media
const setUnsupportedClassEpic = (action$, __, { player }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_CAN_PLAY_THROUGH)
    .do(() => (player.currentType() === HLS_MIME_TYPE && !isHLSSupported()) ? player.addClass(UNSUPPORTED_CSS_CLASS) : player.removeClass(UNSUPPORTED_CSS_CLASS))
    .ignoreElements();


// after playback url is ready and ads have been requested and returned
// start content only if no prerolls are to be played
const startContentAdsEnabledNoPrerollEpic = (action$, store, { player, ads }) => action$
    .ofType(ActionTypes.SET_PLAYBACK_URL)
    .switchMap(() => Observable.combineLatest(
        action$.ofType(ActionTypes.ADS_EVENT_REQUEST_COMPLETE),
    ).take(1))
    .map(() => store.getState()) // get video source state from store
    .filter(state => !!state.videoSource.playbackURL)
    .filter(state => !!state.player.adsEnabled)
    .filter(() => !(ads.prerolls[0] && ads.prerolls[0].getAdCount() > 0)) // sometimes prerolls can have object, but no ads in it
    .filter(() => !ads.prerolls.length) // only start ads when there are no ads from the request
    .do(state => player.src({ 'type': HLS_MIME_TYPE, 'src': state.videoSource.playbackURL }))
    .map(() => ActionCreators.startContent());


// start content right away if ads are disabled
const startContentAdsDisabledEpic = (action$, store, { player, ads }) => action$
    .ofType(ActionTypes.SET_PLAYBACK_URL)
    .map(() => store.getState()) // get video source state from store
    .filter(state => !!state.videoSource.playbackURL)
    .filter(state => !state.player.adsEnabled)
    .do(state => player.src({ 'type': HLS_MIME_TYPE, 'src': state.videoSource.playbackURL }))
    .map(() => ActionCreators.startContent());


// start content after preroll
const startContentAfterPrerollEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.ADS_PREROLL_END)
    .map(() => store.getState().videoSource.playbackURL)
    .filter(Boolean)
    .do(playbackURL => player.src({ 'type': HLS_MIME_TYPE, 'src': playbackURL }))
    .map(() => ActionCreators.startContent());


// reconnect content on force or when midroll ends
const reconnectStreamEpic = (action$, store, { player }) => Observable.merge(
        action$.ofType(ActionTypes.SET_PLAYBACK_URL),
        action$.ofType(ActionTypes.RECONNECT_STREAM),
    )
    .map(() => store.getState().videoSource.playbackURL)
    .filter(Boolean)
    .do(playbackURL => player.src({ 'type': HLS_MIME_TYPE, 'src': playbackURL }))
    .ignoreElements();



export default combineEpics(
    fetchMediaSourceEpic,
    fetchVideoSourceEpic,
    fetchVideoSourceError,
    receiveVideoPlayerDataEpic,
    setSourceFromVideoTagEpic,
    setPlaybackURLEpic,
    setUnsupportedClassEpic,
    startContentAfterPrerollEpic,
    startContentAdsEnabledNoPrerollEpic,
    startContentAdsDisabledEpic,
    reconnectStreamEpic,
    updatePlaybackURLEpic,
);



// WEBPACK FOOTER //
// ./src/player/videoSource/epics.js